import {PurchaseOrderItem} from "../../../../api/Logistics";
import {DataSplitInfo, PageRenderProvider} from "../../../../misc/pdf/parts/PageContext";
import {Page} from "../../../../misc/pdf/parts/Page";
import {PDFHeader} from "../../../../misc/pdf/parts/PDFHeader";
import {orderByAscending, selectMany} from "nate-react-api-helpers";
import React, {useMemo} from "react";
import {makeHardwarePreps, RenderTable, tableCfgToCsv, TblCfg} from "./DoorDetails";
import {PrepsEnriched, WoodDetail} from "../../../../api/Products";
import {HardwareDetailItem} from "./HardwarePrepTable";
import {genericDistanceParser, isMetric} from "../../../../misc/scroller/Table";
import {ImperialDistance} from "../../../../misc/ImperialDistance";
import {MetricDistance} from "../../../../misc/MetricDistance";
import {css} from "@emotion/css";

export type FrameAndDoorDetail = {
    opening: number;
    openingSequence: number;
    openingLocationTransition: string;
    openingType: string;
    openingSubType: string;
    name: string;
    qty: number;
    nominalHeight: string;
    nominalWidth: string;
    frameHardwarePreps: PrepsEnriched;
    doorHardwarePreps: PrepsEnriched;
    doorWoodDetail?: WoodDetail;
    type: string;
    purchaseOrderItem: PurchaseOrderItem;
    frameExactHeight: string;
    doorExactHeight: string;
    hardwarePrepDiagramName: string;
}

export function FrameDetails(props: {
    value: FrameAndDoorDetail[]
    onReady(tf: boolean): void;
}) {
    if(props.value.length === 0) {
        setTimeout(() => props.onReady(true), 100);
        return null;
    }

    return (
        <PageRenderProvider key="frame-details" data={props.value} onReady={props.onReady}>
            {(info: DataSplitInfo<FrameAndDoorDetail>) =>
                <Page orientation="landscape" key={info.pageIndex.toString()}>
                    <div style={{flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch"}}>
                        <PDFHeader noAddress title="Frame Detail"/>
                        <RenderTable<FrameAndDoorDetail> config={tableCfg} pageIndex={info.pageIndex} rows={info.rows} />

                        <div className={disclaimerCss}>
                            <ul>
                                <li>Door thickness to be 1 3/4" unless noted otherwise</li>
                                <li>Apply standard under sizing unless noted otherwise</li>
                                <li>Anchors supplied by Tykel unless noted otherwise</li>
                            </ul>
                        </div>
                    </div>
                </Page>
            }
        </PageRenderProvider>
    )
}

const disclaimerCss = css({
    fontSize: "0.75rem",

    "& ul": {
        listStyle: "none",
    },

    "& li:before": {
        content: '"*"',
        marginRight: "0.5rem",
    }
})

const tableCfg: TblCfg<FrameAndDoorDetail>[] = [
    {name: "Opening", selector: (r) => [r.name]},
    {name: "Qty", selector: (r) => [r.qty.toString()]},
    {name: "Type", selector: (r) => [r.type]},
    {name: "Width", selector: (r) => [r.purchaseOrderItem.dimWidth]},
    {name: "Height", selector: (r) => [r.purchaseOrderItem.dimHeight]},
    {name: "Handing", selector: (r) => [r.purchaseOrderItem.frameHanding]},
    {name: "Label", selector: (r) => [r.purchaseOrderItem.frameLabel]},
    {name: "Series", selector: (r) => [r.purchaseOrderItem.frameSeries]},
    {name: "Material", selector: (r) => [r.purchaseOrderItem.frameMaterial]},
    {name: "Gauge", selector: (r) => [r.purchaseOrderItem.frameGauge]},
    {name: "Screen Elev", selector: (r) => [r.purchaseOrderItem.frameScreenElev]},
    {name: "Profile", selector: (r) => [r.purchaseOrderItem.frameProfile]},
    {name: "Jamb depth", selector: (r) => [r.purchaseOrderItem.frameJambDepth]},
    {name: "Construction", selector: (r) => [r.purchaseOrderItem.frameConstruction]},
    {name: "Elevation", selector: (r) => [r.hardwarePrepDiagramName]},
    {
        name: "Hardware Preps", indent: 1, row: 1, colSpan: 14,
        selector: (r, format) => makeHardwarePreps(r, "frame", format)},
    {
        name: "Exact",
        indent: 1,
        row: 2, colSpan: 14,
        show: (r) => {
            if(!r.frameExactHeight) return false;
            if(!r.purchaseOrderItem.dimHeight) return false;
            try {
                return genericDistanceParser(r.frameExactHeight).universalValue() !== genericDistanceParser(r.purchaseOrderItem.dimHeight).universalValue()
            } catch (e) {
                console.error(e, r.frameExactHeight, r.purchaseOrderItem.dimHeight);
                return true;
            }
        },
        selector: (r) => ["Exact Height: " + r.frameExactHeight],
    },
    {
        name: "Door Thickness",
        indent: 1,
        row: 3, colSpan: 14,
        show: (r, all) => {
            return !!all.find(v => {
                if(!r.purchaseOrderItem.frameDoorThickness) return false;

                try {
                    // normalize b/c sometimes distances are formatted slightly differently (from old systems)
                    const dt = genericDistanceParser(r.purchaseOrderItem.frameDoorThickness);
                    return !standardDoorThicknesses.includes(dt.toString())
                } catch(e) {
                    console.error(e, r.purchaseOrderItem.frameDoorThickness);
                    return true;
                }
            });
        },
        selector: (r) => ["Door Thickness: " + r.purchaseOrderItem.frameDoorThickness]
    },
    {
        name: "Anchor",
        indent: 1,
        row: 4, colSpan: 14,
        show: (r) => {
            const anc = r.frameHardwarePreps["frameAnchors"];
            if(!anc || anc.type !== "frameAnchors") return false;
            if(!anc.name) return false;
            return true;
        },
        selector: (r) => [makeAnchors(r)],
    },
];

const standardDoorThicknesses = [MetricDistance.parse("45mm").toString(), ImperialDistance.parse("1 3/4\"").toString()];

export function frameDetailsCSV(items: FrameAndDoorDetail[]): string[][] {
    return tableCfgToCsv(tableCfg, items);
}

function makeAnchors(r: FrameAndDoorDetail) {
    const anc = r.frameHardwarePreps["frameAnchors"]
    if(!anc || anc.type !== "frameAnchors") return "";

    return `${anc.name} - Qty:${anc.qty}`
}

export function makeFrameDetails(items: PurchaseOrderItem[], hardwareDetail: HardwareDetailItem[] = []): FrameAndDoorDetail[] {
    const fr: FrameAndDoorDetail[] = selectMany(items.filter(v => v.kind === "frame").map(i => {
        if (!i.openings || i.openings.length === 0) {
            return [{
                opening: -i.id,
                openingSequence: 0,
                openingLocationTransition: "",
                openingType: i.frameType,
                qty: i.qty,
                name: "",
                purchaseOrderItem: i,
                type: i.frameType,
                doorHardwarePreps: {},
                doorWoodDetail: i.doorWoodDetail,
                frameHardwarePreps: i.framePreps,
                nominalHeight: i.dimHeight,
                nominalWidth: i.dimWidth,
            }] as FrameAndDoorDetail[];
        }

        return i.openings
            .filter(op => op.kind === "frame")
            .map((op): FrameAndDoorDetail => {
                const hw = hardwareDetail.find(h=> h.openingId === op.id);
                const exact = getExactHeight(op.nominalHeight, hw)

                return {
                    opening: op.id,
                    openingSequence: op.seqNumber,
                    openingLocationTransition: op.locationTransition,
                    openingSubType: "",
                    openingType: op.type,
                    name: op.name,
                    qty: op.qty,
                    purchaseOrderItem: i,
                    type: op.type,
                    doorHardwarePreps: {},
                    doorWoodDetail: i.doorWoodDetail,
                    frameHardwarePreps: op.frameHardwarePreps,
                    nominalHeight: op.nominalHeight,
                    nominalWidth: op.nominalWidth,
                    doorExactHeight: "",
                    frameExactHeight: exact,
                    hardwarePrepDiagramName: hw?.prepDiagramName || "",
                }
            })
    }), v => v);

    orderByAscending(fr, v => v.openingSequence)

    return fr;
}

export function useFrameDetails(items: PurchaseOrderItem[], hardware: HardwareDetailItem[]): FrameAndDoorDetail[] {
    return useMemo(() => makeFrameDetails(items, hardware), [items, hardware]);
}


// different from the door calculator
function getExactHeight(nominalHeight: string, hw?: HardwareDetailItem) {
    const fmt = isMetric(nominalHeight) ? MetricDistance.showMM : ImperialDistance.showInches
    if(!hw) {
        const ht = genericDistanceParser(nominalHeight).universalValue();
        return fmt(ht);
    }

    return fmt(hw.manager.innerFrameHeight());
}